import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Search, Filter, Eye, ChevronDown, ChevronLeft, ChevronRight, Loader2, User, Pause, XCircle } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { cn } from "../../components/ui/utils";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from "../../components/AccessRestricted";
import { api } from "../../utils/api";
import { formatSydneyDateShort } from "../../utils/helpers";
import { useAuth } from "../../context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "../../components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "../../components/ui/dialog";

interface Requisition {
  id: number;
  jobRole: { id: number; roleName: string } | null;
  jobPositions: { id: number; positionCode: string }[];
  department: { id: number; departmentName: string } | null;
  urgency: "High" | "Medium" | "Low" | "Critical";
  status: "OPEN" | "IN_PROGRESS" | "ON_HOLD" | "COMPLETE" | "CANCELLED";
  candidateCount: number;
  interviewingCount: number;
  createdBy: { id: number; name: string } | null;
  createdAt: string;
  requisitionViewers: { id: number; user: { id: number; name: string } }[];
}

const formatStatusLabel = (status: string) => {
  switch (status) {
    case "OPEN":
      return "Open";
    case "IN_PROGRESS":
      return "In Progress";
    case "ON_HOLD":
      return "On Hold";
    case "COMPLETE":
      return "Complete";
    case "CANCELLED":
      return "Cancelled";
    default:
      return status;
  }
};

const getUrgencyStyle = (urgency: string) => {
  switch (urgency) {
    case "Critical":
      return "bg-red-50 text-red-700 border-red-100";
    case "High":
      return "bg-orange-50 text-orange-700 border-orange-100";
    case "Medium":
      return "bg-yellow-50 text-yellow-700 border-yellow-100";
    case "Low":
      return "bg-green-50 text-green-700 border-green-100";
    default:
      return "bg-gray-50 text-gray-600 border-gray-100";
  }
};

const getStatusStyle = (status: string) => {
  switch (status) {
    case "OPEN":
      return "bg-yellow-50 text-yellow-700 border-yellow-100";
    case "IN_PROGRESS":
      return "bg-blue-50 text-blue-700 border-blue-100";
    case "ON_HOLD":
      return "bg-purple-50 text-purple-700 border-purple-100";
    case "COMPLETE":
      return "bg-green-50 text-green-700 border-green-100";
    case "CANCELLED":
      return "bg-red-50 text-red-700 border-red-100";
    default:
      return "bg-gray-50 text-gray-600 border-gray-100";
  }
};

const formatDate = (dateStr: string) => {
  if (!dateStr) return "—";
  return formatSydneyDateShort(dateStr) || "—";
};

const RequisitionsList: React.FC = () => {
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const canReadList = ability.can(ACTIONS.READ, RESOURCES.RECRUITMENT_REQUEST_LIST);
  const canReadModule = ability.can(ACTIONS.READ, RESOURCES.RECRUITMENT_MODULE);
  if (!canReadList && !canReadModule) {
    return <AccessRestricted />;
  }

  const canReadAll = ability.can(ACTIONS.READ, RESOURCES.RECRUITMENT_REQUEST_DETAIL_ALL);
  const isViewerOnly = !canReadList && canReadModule;
  const [requisitions, setRequisitions] = useState<Requisition[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("All Status");
  const [createdByMe, setCreatedByMe] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [reqToCancel, setReqToCancel] = useState<Requisition | null>(null);

  const canUpdateRequisition = ability.can(ACTIONS.UPDATE, RESOURCES.RECRUITMENT_REQUEST);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, createdByMe]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.append("page", String(page));
    params.append("limit", String(limit));
    if (createdByMe) params.append("createdBy", "me");
    if (debouncedSearch.trim()) params.append("search", debouncedSearch.trim());
    if (statusFilter !== "All Status") {
      const statusMap: Record<string, string> = {
        Open: "OPEN",
        "In Progress": "IN_PROGRESS",
        "On Hold": "ON_HOLD",
        Complete: "COMPLETE",
        Cancelled: "CANCELLED",
      };
      params.append("status", statusMap[statusFilter]);
    }
    api
      .get<{ success: boolean; requisitions: Requisition[]; pagination: { total: number; pages: number } }>(
        `/api/requisitions?${params.toString()}`
      )
      .then((data) => {
        if (data.success) {
          setRequisitions(data.requisitions);
          setTotal(data.pagination.total);
          setPages(data.pagination.pages);
        }
      })
      .catch((err) => {
        console.error("Failed to load requisitions:", err);
      })
      .finally(() => setLoading(false));
  }, [createdByMe, debouncedSearch, statusFilter, page, limit]);

  const isSharedRequisition = (req: Requisition) => {
    return req.requisitionViewers?.some((rv) => String(rv.user.id) === currentUser?.id);
  };

  const updateRequisitionStatus = async (reqId: number, status: Requisition["status"]) => {
    setUpdatingId(reqId);
    try {
      const response = await api.put<{ success: boolean; requisition: Requisition }>(
        `/api/requisitions/${reqId}`,
        { status }
      );
      if (response.success) {
        setRequisitions((prev) =>
          prev.map((req) => (req.id === reqId ? response.requisition : req))
        );
        toast.success(`Requisition ${status === "ON_HOLD" ? "placed on hold" : "cancelled"} successfully`);
      }
    } catch (error) {
      console.error(`Failed to update requisition ${reqId} status:`, error);
    } finally {
      setUpdatingId(null);
    }
  };

  const canPlaceOnHold = (status: Requisition["status"]) =>
    status === "OPEN" || status === "IN_PROGRESS";

  const canCancel = (status: Requisition["status"]) =>
    status === "OPEN" || status === "IN_PROGRESS" || status === "ON_HOLD";

  const openCancelDialog = (req: Requisition) => {
    setReqToCancel(req);
    setCancelDialogOpen(true);
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setReqToCancel(null);
  };

  const confirmCancel = async () => {
    if (!reqToCancel) return;
    setCancelDialogOpen(false);
    await updateRequisitionStatus(reqToCancel.id, "CANCELLED");
    setReqToCancel(null);
  };

  const statusOptions = ["All Status", "Open", "In Progress", "On Hold", "Complete", "Cancelled"];

  return (
    <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h4 className="font-bold text-gray-900 text-lg">Requisitions</h4>
          <p className="text-gray-500 text-sm font-medium">
            Manage recruitment requisitions
          </p>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row items-center gap-4">
        <div className="relative w-full sm:max-w-sm">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={18}
          />
          <Input
            placeholder="Search requisitions..."
            className="pl-10 bg-gray-50/50 border-gray-100 focus:bg-white transition-all rounded-xl h-11 text-sm font-medium"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="w-full sm:w-auto bg-gray-50/50 border-gray-100 hover:bg-white transition-all rounded-xl h-11 text-sm font-bold flex items-center justify-between gap-2 px-4"
            >
              <div className="flex items-center gap-2">
                <Filter size={16} className="text-gray-400 shrink-0" />
                <span
                  className={cn(
                    "whitespace-nowrap",
                    statusFilter !== "All Status"
                      ? "text-blue-600"
                      : "text-gray-700"
                  )}
                >
                  {statusFilter === "All Status" ? "Filter" : statusFilter}
                </span>
              </div>
              <ChevronDown size={14} className="text-gray-400 shrink-0" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[200px] p-2 rounded-xl">
            {statusOptions.map((status) => (
              <DropdownMenuItem
                key={status}
                className={cn(
                  "rounded-lg text-sm font-bold cursor-pointer py-2.5",
                  statusFilter === status
                    ? "bg-blue-50 text-blue-600"
                    : "text-gray-600"
                )}
                onClick={() => setStatusFilter(status)}
              >
                {status}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {ability.can(ACTIONS.READ, RESOURCES.RECRUITMENT_REQUEST_DETAIL_CREATED_BY_ME) && (
          <Button
            variant="outline"
            className={cn(
              "w-full sm:w-auto border-gray-100 hover:bg-white transition-all rounded-xl h-11 text-sm font-bold flex items-center justify-center gap-2 px-4",
              createdByMe ? "bg-blue-50 text-blue-600 border-blue-200" : "bg-gray-50/50 text-gray-700"
            )}
            onClick={() => setCreatedByMe((v) => !v)}
          >
            <User size={16} className="shrink-0" />
            <span className="whitespace-nowrap">My Requisitions</span>
          </Button>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-gray-400" />
          <span className="ml-2 text-sm font-medium text-gray-500">Loading requisitions...</span>
        </div>
      )}

      {/* Empty state */}
      {!loading && requisitions.length === 0 && (
        <div className="text-center py-12">
          <p className="text-sm font-medium text-gray-500">
            {isViewerOnly && total === 0
              ? "You have not been invited to any Requisitions"
              : total === 0
                ? "No requisitions found."
                : "No requisitions match your search."}
          </p>
        </div>
      )}

      {/* Desktop Table */}
      {!loading && requisitions.length > 0 && (
        <div className="hidden md:block overflow-x-auto no-scrollbar">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50/50">
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest first:rounded-tl-2xl">
                  Position
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                  Department
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                  Hiring manager
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                  Urgency
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                  Candidates
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                  Created
                </th>
                <th className="px-6 py-4 text-right text-xs font-bold text-gray-400 uppercase tracking-widest last:rounded-tr-2xl">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {requisitions.map((req) => (
                <tr
                  key={req.id}
                  className="hover:bg-gray-50/50 transition-colors"
                >
                  <td className="px-6 py-5">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-gray-900 text-sm">
                          {req.jobRole?.roleName ?? "Unnamed Position"}
                        </p>
                        {isSharedRequisition(req) && (
                          <Badge variant="outline" className="rounded-lg border-0 bg-purple-50 text-purple-700 font-bold px-2 py-0.5 text-[10px]">
                            Shared
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 font-medium mt-0.5">
                        {req.jobPositions.length} position
                        {req.jobPositions.length !== 1 ? "s" : ""}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <span className="text-sm font-bold text-gray-600">
                      {req.department?.departmentName ?? "—"}
                    </span>
                  </td>
                  <td className="px-6 py-5">
                    <span className="text-sm font-bold text-gray-600">
                      {req.createdBy?.name ?? "—"}
                    </span>
                  </td>
                  <td className="px-6 py-5">
                    <Badge
                      variant="outline"
                      className={`rounded-lg border-0 font-bold px-2.5 py-1 text-[10px] ${getUrgencyStyle(
                        req.urgency
                      )}`}
                    >
                      {req.urgency}
                    </Badge>
                  </td>
                  <td className="px-6 py-5">
                    <Badge
                      variant="outline"
                      className={`rounded-lg border-0 font-bold px-2.5 py-1 text-[10px] ${getStatusStyle(
                        req.status
                      )}`}
                    >
                      {formatStatusLabel(req.status)}
                    </Badge>
                  </td>
                  <td className="px-6 py-5">
                    <div>
                      <p className="text-sm font-bold text-gray-900">
                        {req.candidateCount}
                      </p>
                      <p className="text-[10px] text-gray-500 font-medium mt-0.5">
                        {req.interviewingCount} interviewing
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <span className="text-sm font-bold text-gray-600">
                      {formatDate(req.createdAt)}
                    </span>
                  </td>
                  <td className="px-6 py-5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {canUpdateRequisition && canPlaceOnHold(req.status) && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => updateRequisitionStatus(req.id, "ON_HOLD")}
                              disabled={updatingId === req.id}
                              className="text-amber-500 hover:text-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {updatingId === req.id ? (
                                <Loader2 size={16} className="animate-spin" />
                              ) : (
                                <Pause size={16} />
                              )}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>Place on Hold</TooltipContent>
                        </Tooltip>
                      )}
                      {canUpdateRequisition && canCancel(req.status) && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => openCancelDialog(req)}
                              disabled={updatingId === req.id}
                              className="text-red-500 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {updatingId === req.id ? (
                                <Loader2 size={16} className="animate-spin" />
                              ) : (
                                <XCircle size={16} />
                              )}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>Cancel Requisition</TooltipContent>
                        </Tooltip>
                      )}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() =>
                              navigate(`/recruitment/requisitions/${req.id}`)
                            }
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <Eye size={16} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>View Requisition</TooltipContent>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile Card View */}
      {!loading && requisitions.length > 0 && (
        <div className="md:hidden space-y-3">
          {requisitions.map((req) => (
            <div
              key={req.id}
              className="bg-gray-50/50 border border-gray-100 rounded-2xl p-4 space-y-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-gray-900 text-sm">{req.jobRole?.roleName ?? "Unnamed Position"}</p>
                    {isSharedRequisition(req) && (
                      <Badge variant="outline" className="rounded-lg border-0 bg-purple-50 text-purple-700 font-bold px-2 py-0.5 text-[10px]">
                        Shared
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 font-medium mt-0.5">
                    {req.jobPositions.length} position
                    {req.jobPositions.length !== 1 ? "s" : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {canUpdateRequisition && canPlaceOnHold(req.status) && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => updateRequisitionStatus(req.id, "ON_HOLD")}
                          disabled={updatingId === req.id}
                          className="text-amber-500 hover:text-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {updatingId === req.id ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Pause size={16} />
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Place on Hold</TooltipContent>
                    </Tooltip>
                  )}
                  {canUpdateRequisition && canCancel(req.status) && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => openCancelDialog(req)}
                          disabled={updatingId === req.id}
                          className="text-red-500 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {updatingId === req.id ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <XCircle size={16} />
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Cancel Requisition</TooltipContent>
                    </Tooltip>
                  )}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => navigate(`/recruitment/requisitions/${req.id}`)}
                        className="text-gray-400 hover:text-gray-600 transition-colors shrink-0"
                      >
                        <Eye size={16} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>View Requisition</TooltipContent>
                  </Tooltip>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-white rounded-xl px-3 py-2.5 border border-gray-100">
                  <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block mb-0.5">
                    Department
                  </span>
                  <span className="font-bold text-gray-700">{req.department?.departmentName ?? "—"}</span>
                </div>
                <div className="bg-white rounded-xl px-3 py-2.5 border border-gray-100">
                  <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block mb-0.5">
                    Hiring manager
                  </span>
                  <span className="font-bold text-gray-700">{req.createdBy?.name ?? "—"}</span>
                </div>
                <div className="bg-white rounded-xl px-3 py-2.5 border border-gray-100">
                  <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block mb-0.5">
                    Created
                  </span>
                  <span className="font-bold text-gray-700">{formatDate(req.createdAt)}</span>
                </div>
                <div className="bg-white rounded-xl px-3 py-2.5 border border-gray-100">
                  <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block mb-0.5">
                    Urgency
                  </span>
                  <Badge
                    variant="outline"
                    className={`rounded-lg border-0 font-bold px-2 py-0.5 text-[10px] ${getUrgencyStyle(
                      req.urgency
                    )}`}
                  >
                    {req.urgency}
                  </Badge>
                </div>
                <div className="bg-white rounded-xl px-3 py-2.5 border border-gray-100">
                  <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block mb-0.5">
                    Status
                  </span>
                  <Badge
                    variant="outline"
                    className={`rounded-lg border-0 font-bold px-2 py-0.5 text-[10px] ${getStatusStyle(
                      req.status
                    )}`}
                  >
                    {formatStatusLabel(req.status)}
                  </Badge>
                </div>
              </div>

              <div className="flex items-center justify-between pt-1">
                <div className="text-xs">
                  <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px]">
                    Candidates
                  </span>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="font-bold text-gray-900">{req.candidateCount}</span>
                    <span className="text-gray-500 font-medium">
                      ({req.interviewingCount} interviewing)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && total > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-100">
          <p className="text-sm text-gray-500 font-medium">
            Showing{" "}
            <span className="font-bold text-gray-900">
              {(page - 1) * limit + 1}
            </span>{" "}
            -{" "}
            <span className="font-bold text-gray-900">
              {Math.min(page * limit, total)}
            </span>{" "}
            of <span className="font-bold text-gray-900">{total}</span> requisitions
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-9 px-3 rounded-lg border-gray-200 bg-white hover:bg-gray-50 text-sm font-bold"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              <ChevronLeft size={16} className="mr-1" />
              Previous
            </Button>
            <span className="text-sm font-bold text-gray-900 px-3 py-1.5 bg-gray-50 rounded-lg min-w-[2.5rem] text-center">
              {page}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-9 px-3 rounded-lg border-gray-200 bg-white hover:bg-gray-50 text-sm font-bold"
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page >= pages}
            >
              Next
              <ChevronRight size={16} className="ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel Requisition</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel the requisition for{" "}
              <strong>{reqToCancel?.jobRole?.roleName ?? "this position"}</strong>?
              This will change its status to <strong>Cancelled</strong>. This action does not delete the requisition.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button
                variant="outline"
                onClick={closeCancelDialog}
                className="border-gray-200 text-gray-700 hover:bg-gray-50"
              >
                No, Keep It
              </Button>
            </DialogClose>
            <Button
              onClick={confirmCancel}
              disabled={updatingId !== null}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {updatingId === reqToCancel?.id ? (
                <Loader2 size={16} className="animate-spin mr-2" />
              ) : null}
              Yes, Cancel Requisition
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RequisitionsList;
